import { variablesGlobales } from '../../configs/variables';
import { creditOfferStore } from '../../store/creditStore/offerCreditStore';
import { clearLastTouch, getAttributionContext, getOrCreateVisitorId } from '../attribution/attribution';

const UTM_TRACKING_KEYS = [
  'utmReferral',
  'click_id',
  'utm_id',
  'partnerId',
  'visitor',
  'operationIdUtm',
  // Limpieza de marcadores generados por versiones anteriores del frontend.
  'hasNotifiedUtm',
  'utmPostbackCompleted',
];

export function resolveUtmReferral(searchParams) {
  const utmId = searchParams.get('utm_id');
  const clickId = searchParams.get('click_id');
  const visitor = searchParams.get('v');
  const partnerId = searchParams.get('partnerId');
  const gclid = searchParams.get('gclid');
  const fbclid = searchParams.get('fbclid');
  const ttclid = searchParams.get('ttclid');

  return {
    clickId: gclid || fbclid || ttclid || visitor || clickId || null,
    partnerId: partnerId || null,
    utmId: utmId || null,
    typeCustomer: searchParams.get('typeCustomer') || 'nuevo',
  };
}

const PARTNER_REFERRAL_PROTECTION_MS = 30 * 24 * 60 * 60 * 1000;

// Un clic sin partner (por ejemplo, el fbclid que Meta agrega a los clics
// orgánicos) no reemplaza un referral de partner reciente.
function shouldKeepStoredReferral(stored, incoming) {
  if (!stored || incoming.partnerId || incoming.utmId) return false;
  if (!stored.partnerId && !stored.utmId) return false;
  const capturedAt = new Date(stored.capturedAt).getTime();
  return !Number.isNaN(capturedAt) && Date.now() - capturedAt < PARTNER_REFERRAL_PROTECTION_MS;
}

export function persistUtmReferral(referral, storage = window.localStorage) {
  if (!referral?.clickId) return null;
  const stored = getStoredUtmReferral(storage);
  if (shouldKeepStoredReferral(stored, referral)) return stored;
  const normalized = {
    clickId: referral.clickId,
    partnerId: referral.partnerId || null,
    utmId: referral.utmId || null,
    typeCustomer: referral.typeCustomer || 'nuevo',
    capturedAt: new Date().toISOString(),
  };
  storage.setItem('utmReferral', JSON.stringify(normalized));
  // El operationId pertenece al crédito anterior; se reemplaza únicamente cuando
  // la nueva firma devuelve el settlement actual.
  storage.removeItem('operationIdUtm');
  storage.setItem('click_id', normalized.clickId);
  if (normalized.partnerId) storage.setItem('partnerId', normalized.partnerId);
  if (normalized.utmId) storage.setItem('utm_id', normalized.utmId);
  return normalized;
}

function readLegacyReferral(storage) {
  try {
    const serialized = storage.getItem('utmReferral');
    if (serialized) return JSON.parse(serialized);
  } catch (error) {
    console.warn('[UtmLead] No fue posible leer la atribución guardada', error);
  }
  const clickId = storage.getItem('click_id');
  if (!clickId) return null;
  return {
    clickId,
    partnerId: storage.getItem('partnerId'),
    utmId: storage.getItem('utm_id'),
    typeCustomer: 'nuevo',
    capturedAt: new Date().toISOString(),
  };
}

// Respaldo cuando un localStorage.clear() del flujo borró el referral: se
// reconstruye con el último toque, que vive también en una cookie.
function referralFromLastTouch() {
  const { lastTouch } = getAttributionContext();
  if (!lastTouch?.clickId) return null;
  return {
    clickId: lastTouch.clickId,
    partnerId: lastTouch.partnerId || null,
    utmId: lastTouch.utmId || null,
    typeCustomer: lastTouch.typeCustomer || 'nuevo',
    capturedAt: lastTouch.capturedAt,
  };
}

export function getStoredUtmReferral(storage = window.localStorage) {
  const referral = readLegacyReferral(storage) || referralFromLastTouch();
  if (!referral) return null;
  // visitorId une este referral con el ID de visitante (gvid) en el backend.
  return { ...referral, visitorId: getOrCreateVisitorId() };
}

export function storeUtmSettlement(response, storage = window.localStorage) {
  const settlement = response?.utmAttribution;
  if (settlement?.operationIdUtm) storage.setItem('operationIdUtm', settlement.operationIdUtm);
  if (settlement?.organic) storage.removeItem('operationIdUtm');
  return settlement || null;
}

export function getPostbackContext() {
  const getCookie = (name) => {
    if (typeof document === 'undefined') return null;
    const prefix = `${name}=`;
    const cookie = document.cookie
      .split('; ')
      .find((value) => value.startsWith(prefix));
    return cookie ? decodeURIComponent(cookie.slice(prefix.length)) : null;
  };

  return {
    version: 1,
    facebook: {
      fbc: getCookie('_fbc'),
      fbp: getCookie('_fbp'),
    },
    tiktok: {
      eventName: variablesGlobales.eventNameLoanSignedTiktok || 'LoanSigned',
      ttp: getCookie('_ttp'),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      pageUrl: typeof window !== 'undefined' ? window.location.href : null,
    },
  };
}

async function sha256(value) {
  const data = new TextEncoder().encode(value);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(hashBuffer))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

function normalizePhone(phone) {
  const digits = String(phone || '').replace(/\D/g, '');
  if (digits.startsWith('57')) return digits;
  if (digits.startsWith('3') && digits.length === 10) return `57${digits}`;
  return digits;
}

export async function trackTiktokSignedPixel({ creditId, value } = {}) {
  try {
    if (typeof window === 'undefined') return false;
    if (localStorage.getItem('utm_id') !== variablesGlobales.partnerUtmNameTiktokAds) return false;
    if (!creditId || !window.ttq || typeof window.ttq.track !== 'function') return false;

    const email = String(localStorage.getItem('email') || '').trim().toLowerCase();
    const phone = normalizePhone(localStorage.getItem('phone'));
    const [hashedEmail, hashedPhone] = await Promise.all([
      email ? sha256(email) : '',
      phone ? sha256(`+${phone}`) : '',
    ]);
    const approvedQuota = value ?? await creditOfferStore.getState().loadApprovedQuota();
    const eventName = variablesGlobales.eventNameLoanSignedTiktok || 'LoanSigned';

    if (typeof window.ttq.identify === 'function') {
      window.ttq.identify({
        ...(hashedEmail && { email: hashedEmail }),
        ...(hashedPhone && { phone_number: hashedPhone }),
      });
    }

    window.ttq.track(
      eventName,
      { value: Number(approvedQuota || 0), currency: 'COP' },
      { event_id: creditId },
    );
    return true;
  } catch (error) {
    console.warn('[TikTok Pixel] No fue posible registrar LoanSigned', error);
    return false;
  }
}

export function markUtmPostbackCompleted(storage = window.localStorage) {
  storage.setItem('utmPostbackCompleted', 'true');
}

export function clearUtmTracking(storage = window.localStorage) {
  UTM_TRACKING_KEYS.forEach((key) => storage.removeItem(key));
  // El último toque ya se usó en este crédito; si no, se reconstruiría el
  // referral desde la cookie para el siguiente.
  clearLastTouch();
}
